import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
    WEEKDAY_KEYS,
    addDaySlot,
    copyTimeFrom,
    removeSlot,
    sortScheduleSlots,
    toggleWeekday,
    updateSlot,
} from '../../../utils/weekdaySchedule';

const DAY_LABEL = {
    monday: 'gym.courses.addRecurrenceModal.daysOfWeek.monday',
    tuesday: 'gym.courses.addRecurrenceModal.daysOfWeek.tuesday',
    wednesday: 'gym.courses.addRecurrenceModal.daysOfWeek.wednesday',
    thursday: 'gym.courses.addRecurrenceModal.daysOfWeek.thursday',
    friday: 'gym.courses.addRecurrenceModal.daysOfWeek.friday',
    saturday: 'gym.courses.addRecurrenceModal.daysOfWeek.saturday',
    sunday: 'gym.courses.addRecurrenceModal.daysOfWeek.sunday',
};

export default function WeekdayScheduleEditor({ slots, onChange, defaults, compact = false }) {
    const { t } = useTranslation();
    const selected = new Set(slots.map((slot) => slot.day));
    const ordered = sortScheduleSlots(slots);
    const grouped = WEEKDAY_KEYS.filter((day) => selected.has(day));

    return (
        <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('gym.courses.weekdaySchedule.hint')}
            </p>
            <div className="flex flex-wrap gap-2">
                {WEEKDAY_KEYS.map((day) => {
                    const active = selected.has(day);
                    return (
                        <button
                            key={day}
                            type="button"
                            aria-pressed={active}
                            onClick={() => onChange(toggleWeekday(slots, day, defaults))}
                            className={`rounded-lg border-2 px-3 py-1.5 text-sm font-medium transition-colors ${
                                active
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200'
                                    : 'border-gray-300 text-gray-700 hover:border-indigo-300 dark:border-gray-700 dark:text-gray-200'
                            }`}
                        >
                            {t(DAY_LABEL[day])}
                        </button>
                    );
                })}
            </div>

            <div className={compact ? 'grid grid-cols-1 gap-2 xl:grid-cols-2' : 'space-y-3'}>
            {grouped.map((day) => {
                const rows = ordered.filter((item) => item.slot.day === day);
                return (
                    <div
                        key={day}
                        className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/60"
                    >
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                {t(DAY_LABEL[day])}
                            </p>
                            {slots.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => onChange(copyTimeFrom(slots, rows[0].slot))}
                                    className="text-xs font-medium text-indigo-700 hover:underline dark:text-indigo-300"
                                >
                                    {t('gym.courses.weekdaySchedule.copyAll')}
                                </button>
                            )}
                        </div>
                        <div className="space-y-2">
                            {rows.map(({ slot, index }) => (
                                <div key={`${day}-${index}`} className="flex flex-wrap items-center gap-2">
                                    <input
                                        type="time"
                                        value={slot.start_time}
                                        onChange={(event) =>
                                            onChange(updateSlot(slots, index, 'start_time', event.target.value))
                                        }
                                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                                        aria-label={t('gym.courses.createModal.startTimeLabel')}
                                        required
                                    />
                                    <span className="text-gray-400">–</span>
                                    <input
                                        type="time"
                                        value={slot.end_time}
                                        onChange={(event) =>
                                            onChange(updateSlot(slots, index, 'end_time', event.target.value))
                                        }
                                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                                        aria-label={t('gym.courses.createModal.endTimeLabel')}
                                        required
                                    />
                                    {rows.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => onChange(removeSlot(slots, index))}
                                            className="rounded-lg p-2 text-gray-500 hover:bg-white hover:text-red-600 dark:hover:bg-gray-900"
                                            title={t('gym.courses.weekdaySchedule.removeSlot')}
                                        >
                                            <Trash2 size={16} aria-hidden />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => onChange(addDaySlot(slots, day))}
                            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:underline dark:text-indigo-300"
                        >
                            <Plus size={14} aria-hidden />
                            {t('gym.courses.weekdaySchedule.addSlot')}
                        </button>
                    </div>
                );
            })}
            </div>
        </div>
    );
}
